import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowsRotate,
  faDownload,
  faEye,
  faLink,
  faPen,
  faPowerOff,
  faSearch,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  exportUploadS3PublicShareTokens,
  listUploadS3PublicShareTokens,
  regenerateUploadS3PublicShareToken,
  toggleUploadS3PublicShareTokenActive,
  updateUploadS3PublicShareToken,
  viewUploadS3PublicShareTokenSourceFile,
} from "../../services/uploadS3PublicShareTokenApi";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("en-PH");
};

const UploadS3PublicShareTokenPage = () => {
  const [rows, setRows] = useState([]);
  const [sourceTables, setSourceTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sourceTable, setSourceTable] = useState("");
  const [active, setActive] = useState("");
  const [revoked, setRevoked] = useState("");
  const [expired, setExpired] = useState("");

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  const [editOpen, setEditOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editForm, setEditForm] = useState({});

  const [sourceModalOpen, setSourceModalOpen] = useState(false);
  const [sourceData, setSourceData] = useState(null);
  const [sourceLoading, setSourceLoading] = useState(false);

  const [actionBusyId, setActionBusyId] = useState(null);

  const totalPages = useMemo(() => Math.max(1, lastPage || Math.ceil(total / perPage)), [lastPage, total, perPage]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await listUploadS3PublicShareTokens({
        page,
        per_page: perPage,
        search,
        source_table: sourceTable,
        active,
        revoked,
        expired,
      });

      const payload = response.data?.data || {};
      setRows(payload.rows || []);
      setPage(payload.current_page || 1);
      setPerPage(payload.per_page || 20);
      setTotal(payload.total || 0);
      setLastPage(payload.last_page || 1);
      setSourceTables(response.data?.filters?.source_tables || []);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load Upload S3 Public Share Token records.");
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search, sourceTable, active, revoked, expired]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  const handleExport = async () => {
    try {
      const response = await exportUploadS3PublicShareTokens({
        search,
        source_table: sourceTable,
        active,
        revoked,
        expired,
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `upload_s3_public_share_token_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to export records.");
    }
  };

  const openEditModal = (row) => {
    setEditingRow(row);
    setEditForm({
      share_token: row.share_token || "",
      source_table: row.source_table || "",
      upload_id: row.upload_id || "",
      source_file_id: row.source_file_id ?? "",
      created_by_user_id: row.created_by_user_id ?? "",
      expires_at: row.expires_at ? String(row.expires_at).slice(0, 19) : "",
      revoked_at: row.revoked_at ? String(row.revoked_at).slice(0, 19) : "",
      download_count: row.download_count ?? 0,
      max_download_count: row.max_download_count ?? "",
      last_downloaded_at: row.last_downloaded_at ? String(row.last_downloaded_at).slice(0, 19) : "",
      active: row.active ?? 1,
    });
    setEditOpen(true);
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editingRow) return;

    setEditLoading(true);
    setError("");
    try {
      const payload = {
        share_token: String(editForm.share_token || "").trim(),
        source_table: String(editForm.source_table || "").trim(),
        upload_id: String(editForm.upload_id || "").trim(),
        source_file_id: editForm.source_file_id === "" ? null : Number(editForm.source_file_id),
        created_by_user_id: editForm.created_by_user_id === "" ? null : Number(editForm.created_by_user_id),
        expires_at: editForm.expires_at === "" ? null : editForm.expires_at,
        revoked_at: editForm.revoked_at === "" ? null : editForm.revoked_at,
        download_count: editForm.download_count === "" ? 0 : Number(editForm.download_count),
        max_download_count: editForm.max_download_count === "" ? null : Number(editForm.max_download_count),
        last_downloaded_at: editForm.last_downloaded_at === "" ? null : editForm.last_downloaded_at,
        active: Number(editForm.active),
      };

      await updateUploadS3PublicShareToken(editingRow.id, payload);
      setEditOpen(false);
      setEditingRow(null);
      fetchData();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to update record.");
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleActive = async (row) => {
    setActionBusyId(row.id);
    setError("");
    try {
      await toggleUploadS3PublicShareTokenActive(row.id);
      fetchData();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to toggle active status.");
    } finally {
      setActionBusyId(null);
    }
  };

  const handleRegenerate = async (row) => {
    if (!window.confirm("Regenerate token now? Old public link will stop working.")) return;

    setActionBusyId(row.id);
    setError("");
    try {
      await regenerateUploadS3PublicShareToken(row.id);
      fetchData();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to regenerate token.");
    } finally {
      setActionBusyId(null);
    }
  };

  const openSourceView = async (row) => {
    setSourceModalOpen(true);
    setSourceLoading(true);
    setSourceData(null);
    setError("");
    try {
      const response = await viewUploadS3PublicShareTokenSourceFile(row.id);
      setSourceData(response.data?.data || null);
    } catch (e) {
      setError(e.response?.data?.message || "Failed to load source file details.");
    } finally {
      setSourceLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b border-slate-200 bg-white px-6 py-5">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Upload S3 Public Share Token</h1>
            <p className="text-sm text-slate-600">Advanced management module for token lifecycle, filtering, export, regeneration, and source file visibility.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={fetchData}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
            >
              <FontAwesomeIcon icon={faArrowsRotate} /> Refresh
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
            >
              <FontAwesomeIcon icon={faDownload} /> Export
            </button>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        {error && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{typeof error === "string" ? error : JSON.stringify(error)}</div>
        )}

        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <form onSubmit={handleSearch} className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <div className="md:col-span-2 relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search token, upload_id, source_table..."
                className="w-full rounded-xl border border-slate-200 py-2 pl-9 pr-3 text-sm"
              />
            </div>
            <select value={sourceTable} onChange={(e) => { setSourceTable(e.target.value); setPage(1); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <option value="">All Source Tables</option>
              {sourceTables.map((table) => (
                <option key={table} value={table}>{table}</option>
              ))}
            </select>
            <select value={active} onChange={(e) => { setActive(e.target.value); setPage(1); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <option value="">All Active Status</option>
              <option value="1">Active</option>
              <option value="0">Inactive</option>
            </select>
            <select value={revoked} onChange={(e) => { setRevoked(e.target.value); setPage(1); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <option value="">All Revoked Status</option>
              <option value="0">Not Revoked</option>
              <option value="1">Revoked</option>
            </select>
            <select value={expired} onChange={(e) => { setExpired(e.target.value); setPage(1); }} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
              <option value="">All Expiry Status</option>
              <option value="0">Not Expired</option>
              <option value="1">Expired</option>
            </select>
            <div className="md:col-span-6 flex items-center gap-2">
              <button type="submit" className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">Search</button>
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setSearch("");
                  setSourceTable("");
                  setActive("");
                  setRevoked("");
                  setExpired("");
                  setPage(1);
                }}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100"
              >
                Clear
              </button>
            </div>
          </form>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 text-sm text-slate-500">
            <span>{loading ? "Loading records..." : `${total} token record(s)`}</span>
            <div className="flex items-center gap-2">
              <span>Rows</span>
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="rounded-lg border border-slate-200 px-2 py-1 text-sm"
              >
                {PER_PAGE_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-[0.15em] text-slate-500">
                <tr>
                  <th className="px-3 py-3 text-left">ID</th>
                  <th className="px-3 py-3 text-left">Source</th>
                  <th className="px-3 py-3 text-left">Upload ID</th>
                  <th className="px-3 py-3 text-left">Token</th>
                  <th className="px-3 py-3 text-center">Downloads</th>
                  <th className="px-3 py-3 text-left">Expires</th>
                  <th className="px-3 py-3 text-left">Status</th>
                  <th className="px-3 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">Loading...</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-400">No records found.</td></tr>
                ) : rows.map((row) => (
                  <tr key={row.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2">{row.id}</td>
                    <td className="px-3 py-2">{row.source_table}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.upload_id}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs">{row.share_token?.slice(0, 14)}...{row.share_token?.slice(-6)}</span>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(row.public_share_url || "")}
                          className="text-indigo-600 hover:text-indigo-800"
                          title="Copy public URL"
                        >
                          <FontAwesomeIcon icon={faLink} />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">{row.download_count || 0}</td>
                    <td className="px-3 py-2">{formatDate(row.expires_at)}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${row.active ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-600"}`}>{row.active ? "Active" : "Inactive"}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold ${row.revoked_at ? "bg-rose-100 text-rose-700" : "bg-indigo-100 text-indigo-700"}`}>{row.revoked_at ? "Revoked" : "Open"}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={() => openSourceView(row)} className="px-2 py-1 rounded bg-sky-100 hover:bg-sky-200 text-sky-700 text-xs font-semibold">
                          <FontAwesomeIcon icon={faEye} /> View File
                        </button>
                        <button type="button" onClick={() => openEditModal(row)} className="px-2 py-1 rounded bg-amber-100 hover:bg-amber-200 text-amber-700 text-xs font-semibold">
                          <FontAwesomeIcon icon={faPen} /> Edit
                        </button>
                        <button
                          type="button"
                          disabled={actionBusyId === row.id}
                          onClick={() => handleToggleActive(row)}
                          className="px-2 py-1 rounded bg-slate-200 hover:bg-slate-300 text-slate-700 text-xs font-semibold"
                        >
                          <FontAwesomeIcon icon={faPowerOff} /> {row.active ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          type="button"
                          disabled={actionBusyId === row.id}
                          onClick={() => handleRegenerate(row)}
                          className="px-2 py-1 rounded bg-violet-100 hover:bg-violet-200 text-violet-700 text-xs font-semibold"
                        >
                          <FontAwesomeIcon icon={faArrowsRotate} /> Regenerate
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
            <div className="flex items-center gap-2">
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 rounded border border-slate-200 text-sm disabled:opacity-50">Prev</button>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1.5 rounded border border-slate-200 text-sm disabled:opacity-50">Next</button>
            </div>
          </div>
        </div>
      </div>

      {editOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[88vh] overflow-y-auto border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold">Edit Token Record #{editingRow?.id}</h2>
              <button type="button" onClick={() => setEditOpen(false)} className="text-slate-500 hover:text-slate-700">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <form onSubmit={saveEdit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">share_token</span>
                <input value={editForm.share_token || ""} onChange={(e) => setEditForm((s) => ({ ...s, share_token: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
              </label>
              <label className="text-sm">
                <span className="block text-slate-600 mb-1">source_table</span>
                <select value={editForm.source_table || ""} onChange={(e) => setEditForm((s) => ({ ...s, source_table: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2">
                  {sourceTables.map((table) => (<option key={table} value={table}>{table}</option>))}
                </select>
              </label>
              <label className="text-sm md:col-span-2">
                <span className="block text-slate-600 mb-1">upload_id</span>
                <input value={editForm.upload_id || ""} onChange={(e) => setEditForm((s) => ({ ...s, upload_id: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" />
              </label>
              <label className="text-sm"><span className="block text-slate-600 mb-1">source_file_id</span><input type="number" value={editForm.source_file_id ?? ""} onChange={(e) => setEditForm((s) => ({ ...s, source_file_id: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" /></label>
              <label className="text-sm"><span className="block text-slate-600 mb-1">created_by_user_id</span><input type="number" value={editForm.created_by_user_id ?? ""} onChange={(e) => setEditForm((s) => ({ ...s, created_by_user_id: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" /></label>
              <label className="text-sm"><span className="block text-slate-600 mb-1">expires_at (YYYY-MM-DD HH:mm:ss)</span><input value={editForm.expires_at || ""} onChange={(e) => setEditForm((s) => ({ ...s, expires_at: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" /></label>
              <label className="text-sm"><span className="block text-slate-600 mb-1">revoked_at (YYYY-MM-DD HH:mm:ss)</span><input value={editForm.revoked_at || ""} onChange={(e) => setEditForm((s) => ({ ...s, revoked_at: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" /></label>
              <label className="text-sm"><span className="block text-slate-600 mb-1">download_count</span><input type="number" min="0" value={editForm.download_count ?? 0} onChange={(e) => setEditForm((s) => ({ ...s, download_count: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" /></label>
              <label className="text-sm"><span className="block text-slate-600 mb-1">max_download_count</span><input type="number" min="0" value={editForm.max_download_count ?? ""} onChange={(e) => setEditForm((s) => ({ ...s, max_download_count: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" /></label>
              <label className="text-sm"><span className="block text-slate-600 mb-1">last_downloaded_at</span><input value={editForm.last_downloaded_at || ""} onChange={(e) => setEditForm((s) => ({ ...s, last_downloaded_at: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-3 py-2" /></label>
              <label className="text-sm"><span className="block text-slate-600 mb-1">active</span><select value={String(editForm.active ?? 1)} onChange={(e) => setEditForm((s) => ({ ...s, active: Number(e.target.value) }))} className="w-full rounded-lg border border-slate-200 px-3 py-2"><option value="1">1</option><option value="0">0</option></select></label>

              <div className="md:col-span-2 flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setEditOpen(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-slate-600">Cancel</button>
                <button type="submit" disabled={editLoading} className="px-4 py-2 rounded-lg bg-indigo-600 text-white font-semibold disabled:opacity-60">{editLoading ? "Saving..." : "Save Changes"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {sourceModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[88vh] overflow-y-auto border border-slate-200 shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold">Source File Viewer</h2>
              <button type="button" onClick={() => setSourceModalOpen(false)} className="text-slate-500 hover:text-slate-700">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {sourceLoading ? (
                <p className="text-sm text-slate-500">Loading source file details...</p>
              ) : !sourceData ? (
                <p className="text-sm text-slate-500">No source data found.</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-slate-500">Source Table</p>
                      <p className="font-semibold text-slate-800">{sourceData.token?.source_table}</p>
                    </div>
                    <div className="rounded-lg border border-slate-200 p-3">
                      <p className="text-slate-500">Upload ID</p>
                      <p className="font-mono text-xs text-slate-800">{sourceData.token?.upload_id}</p>
                    </div>
                  </div>

                  {sourceData.preview_url ? (
                    <div className="flex items-center gap-2">
                      <a href={sourceData.preview_url} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold">
                        <FontAwesomeIcon icon={faEye} /> Open/Preview Source File
                      </a>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No preview URL available.</p>
                  )}

                  <div className="rounded-lg border border-slate-200 p-3">
                    <p className="text-slate-500 text-xs uppercase tracking-wide mb-2">Raw Source Record</p>
                    <pre className="text-xs text-slate-700 whitespace-pre-wrap break-all">{JSON.stringify(sourceData.source, null, 2)}</pre>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadS3PublicShareTokenPage;
